import {Tab, Tabs, useMediaQuery, useTheme} from '@mui/material';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useLocation, useNavigate} from 'react-router';
import {TabContext, TabPanel} from "@mui/lab";
import CompanyProfile from "../../components/companyProfile/CompanyProfile";
import UserManagement from "../../components/user/manageUsers/UserManagement";
import PaymentMethods from "../../components/paymentMethods/PaymentMethods";
import SubscriptionManager from "../../components/subscriptions/SubscriptionManager";
import {hasAdminAccess, isBuyerAccount, isSupplierAccount} from "../../util/AccountUtils";
import {SecurSpaceContainer, SecurSpaceReportContainer} from "../../components/common";
import {TabContextContainer} from "../../components/common/reports/tabs/TabContextContainer";
import {TabsContainer} from "../../components/common/reports/tabs/TabsContainer";
import {styled} from "@mui/material/styles";
import {BuyerNotificationSettings} from "./BuyerNotificationSettings";
import {SupplierNotificationSettings} from "./SupplierNotificationSettings";
import {usePathTabMapping} from "../../hooks/routing/usePathTabMapping";

const TabsBorderBottomGrey200 = styled(Tabs)(({theme}) => ({
  borderBottom: `${theme.palette.grey[200]} solid 0.07rem`,
  '& .MuiButtonBase-root.MuiTab-root ':{
    fontSize:'1em',
    // Keep each label on one line; below `md` the Tabs switch to `scrollable`,
    // so long labels ('Payout Bank Account') scroll instead of wrapping and
    // unbreakable ones ('Notifications') stop getting clipped by the scroller's
    // `overflow: hidden`.
    whiteSpace: 'nowrap'
  }
}));

const TAB_ROUTES = [
  { navPath: '/account/company-profile', tabName: 'company' },
  { navPath: '/account/payment-methods', tabName: 'payment' },
  { navPath: '/account/user-management', tabName: 'users' },
  { navPath: '/account/notification-settings', tabName: 'notifications' },
];

const AccountOverview = ({ initialTab = 'company', account, handleAccountUpdated }) => {

  const theme = useTheme();
  // `md` is the knob: five uppercase labels need ~670px before `fullWidth`
  // starts squeezing them, which is inside the `md` range.
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  const { currentTab, handleTabChange } = usePathTabMapping(TAB_ROUTES, initialTab);

  return (
    <SecurSpaceContainer maxWidth={'xl'}>
      <SecurSpaceReportContainer>
        <TabContextContainer>
          <TabContext value={currentTab}>
            <TabsContainer>
              <TabsBorderBottomGrey200
                variant={isNarrow ? 'scrollable' : 'fullWidth'}
                scrollButtons={false}
                value={currentTab}
                onChange={handleTabChange}
                aria-label={'Account Tabs'}
                textColor={'secondary'}
              >
                <Tab value={'company'} label='Company'/>
                <Tab value={'payment'} label={isSupplierAccount(account) ? 'Payout Bank Account' : 'Payment Methods'}/>
                { hasAdminAccess(account) && isSupplierAccount(account) &&
                  <Tab value={'subscriptions'} label='Subscriptions'/>
                }
                <Tab value={'users'} label='Users'/>
                <Tab value={'notifications'} label='Notifications'/>
              </TabsBorderBottomGrey200>
              <TabPanel value={'company'}>
                <CompanyProfile account={account} handleAccountUpdated={handleAccountUpdated}/>
              </TabPanel>
              <TabPanel value={'payment'}>
                <PaymentMethods account={account}/>
              </TabPanel>
              <TabPanel value={'subscriptions'}>
                <SubscriptionManager account={account} />
              </TabPanel>
              <TabPanel value={'users'}>
                <UserManagement account={account} />
              </TabPanel>
              <TabPanel value={'notifications'}>
                { isBuyerAccount(account) && <BuyerNotificationSettings /> }
                { isSupplierAccount(account) && <SupplierNotificationSettings /> }

              </TabPanel>
            </TabsContainer>
          </TabContext>
        </TabContextContainer>
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

AccountOverview.propTypes = {
  initialTab: PropTypes.string,
  account: PropTypes.object.isRequired,
  handleAccountUpdated: PropTypes.func.isRequired,
};

export default AccountOverview;
